import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControlLabel,
  Switch,
  Grid,
  Alert,
  CircularProgress,
  IconButton,
  Box,
  Typography,
  InputAdornment,
} from '@mui/material';
import {
  Close as CloseIcon,
  Visibility,
  VisibilityOff,
} from '@mui/icons-material';
import { useForm } from 'react-hook-form';
import apiService from '../../services/api';
import { toast } from 'react-toastify';
import FormTextField, { RequiredLabel } from '../common/FormTextField';

export interface UserFormData {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  password: string;
  // isActive: boolean;
}

interface CreateUserDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: () => void;
}

const UserEditDialog: React.FC<CreateUserDialogProps> = ({
  open,
  onClose,
  onSave,
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = React.useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
    control,
  } = useForm<UserFormData>({
    mode: 'onChange',
    reValidateMode: 'onChange',
    defaultValues: {
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      password: '',
      // isActive: true,
    },
  });

  // const isActive = watch('isActive');

  const onSubmit = async (data: UserFormData) => {
    setLoading(true);
    setError('');

    try {
      const payload: UserFormData = {
        firstName: data.firstName.trim(),
        lastName: data.lastName.trim(),
        email: data.email.trim(),
        phone: data.phone.trim(),
        password: data.password.trim(),
        // isActive: data.isActive,
      };

      await apiService.createUser(payload);
      toast.success('User Created sucessfully');
      onSave();
      onClose();
    } catch (err: any) {
      setError(
        err?.response?.data?.message ||
          'Failed to create user. Please try again.'
      );
    } finally {
      setLoading(false);
    }
  };
  const handleClose = () => {
    if (!loading) {
      onClose();
      setError('');
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      {/* <DialogTitle>Create a User</DialogTitle>
      <IconButton onClick={handleClose} disabled={loading}>
        <CloseIcon />
      </IconButton> */}

      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Box>
          <Typography sx={{ fontSize: '1.25rem', fontWeight: 600 }}>
            Create a User
          </Typography>
        </Box>
        <IconButton onClick={handleClose} disabled={loading}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogContent>
          {error && <Alert severity="error">{error}</Alert>}

          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <FormTextField
                name="firstName"
                control={control}
                label={<RequiredLabel label="First Name" />}
                fullWidth
                disabled={loading}
                rules={{
                  required: 'First name is required',
                  minLength: {
                    value: 3,
                    message: 'Minimum 3 characters',
                  },
                  maxLength: {
                    value: 100,
                    message: 'Maximum 100 character',
                  },
                }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormTextField
                name="lastName"
                control={control}
                label={<RequiredLabel label="Last Name" />}
                fullWidth
                disabled={loading}
                rules={{
                  required: 'Last name is required',
                  minLength: {
                    value: 3,
                    message: 'Minimum 3 characters',
                  },
                  maxLength: {
                    value: 100,
                    message: 'Maximum 100 character',
                  },
                }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormTextField
                name="email"
                control={control}
                label={<RequiredLabel label="Email" />}
                fullWidth
                disabled={loading}
                rules={{
                  required: 'Email is required',
                  pattern: {
                    value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                    message: 'Invalid email',
                  },
                }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormTextField
                name="phone"
                control={control}
                rules={{
                  required: 'Contact Number is required',
                  pattern: {
                    value: /^\d{9,15}$/,
                    message:
                      'Contact number must contain only numbers (9–15 digits)',
                  },
                }}
                inputProps={{
                  inputMode: 'numeric',
                  pattern: '[0-9]*',
                }}
                onKeyDown={e => {
                  if (
                    !/[0-9]/.test(e.key) &&
                    !['Backspace', 'Tab', 'ArrowLeft', 'ArrowRight'].includes(
                      e.key
                    )
                  ) {
                    e.preventDefault();
                  }
                }}
                label={<RequiredLabel label="Contact Number" />}
                fullWidth
                disabled={loading}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                {...register('password', {
                  required: 'Password is required',
                  minLength: {
                    value: 6,
                    message: 'Password must be at least 6 characters',
                  },

                  validate: value =>
                    !/\s/.test(value) || 'Password must not contain spaces',
                })}
                label={<RequiredLabel label="Password" />}
                type={showPassword ? 'text' : 'password'}
                fullWidth
                margin="normal"
                error={!!errors.password}
                helperText={errors.password?.message}
                onChange={e => {
                  e.target.value = e.target.value.replace(/\s/g, '');
                }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPassword(prev => !prev)}
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button
            onClick={onClose}
            disabled={loading}
            variant="outlined"
            color="inherit"
            sx={{
              backgroundColor: 'rgba(211, 47, 47, 0.08)',
              color: '#d32f2f',
              fontWeight: 500,
              '&:hover': {
                backgroundColor: 'rgba(211, 47, 47, 0.15)',
              },
            }}
          >
            Cancel
          </Button>

          <Button
            type="submit"
            variant="contained"
            disabled={loading}
            startIcon={loading ? <CircularProgress size={18} /> : undefined}
          >
            {loading ? 'Creating...' : 'Create'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default UserEditDialog;
